import { ChevronRight, AlertTriangle } from "lucide-react";
import { PIPELINE, STAGE_LABEL, LAB_STAGE } from "../lib/lab-constants";
import { cn } from "../lib/utils";

/**
 * Stage strip for lab orders.
 *
 * The reference console shows seventeen status tiles, most of them permanently
 * zero — a wall of numbers you have to read in full to learn nothing. Orders
 * actually move through four stages, so that's what's shown, with chevrons to
 * make the direction of travel obvious. Individual statuses stay reachable
 * through the Status filter.
 *
 * Exceptions get their own callout rather than a tile, because "3 orders need
 * chasing" is a different kind of fact from "12 orders are in processing".
 */
export default function LabPipeline({ counts = {}, value, onChange }) {
  const attention = counts[LAB_STAGE.ATTENTION] || 0;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-stretch gap-2">
        {/* All */}
        <button
          type="button"
          onClick={() => onChange(null)}
          className={cn(
            "flex min-w-[7rem] flex-col justify-between rounded-lg border px-4 py-3 text-left transition-colors",
            !value
              ? "border-primary bg-primary/10"
              : "border-border bg-card hover:bg-muted/40"
          )}
        >
          <span className="text-xs font-medium text-muted-foreground">All orders</span>
          <span className="mt-1 font-serif text-2xl font-semibold tabular-nums">{counts.all ?? 0}</span>
        </button>

        {PIPELINE.map((stage, i) => {
          const active = value === stage;
          return (
            <div key={stage} className="flex items-stretch gap-2">
              <ChevronRight className="my-auto h-4 w-4 shrink-0 text-muted-foreground/40" aria-hidden="true" />
              <button
                type="button"
                onClick={() => onChange(active ? null : stage)}
                className={cn(
                  "flex min-w-[8rem] flex-col justify-between rounded-lg border px-4 py-3 text-left transition-colors",
                  active
                    ? "border-primary bg-primary/10"
                    : "border-border bg-card hover:bg-muted/40"
                )}
              >
                <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  <span
                    className={cn(
                      "flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-semibold",
                      active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                    )}
                  >
                    {i + 1}
                  </span>
                  {STAGE_LABEL[stage]}
                </span>
                <span className="mt-1 font-serif text-2xl font-semibold tabular-nums">
                  {counts[stage] ?? 0}
                </span>
              </button>
            </div>
          );
        })}
      </div>

      {attention > 0 && (
        <button
          type="button"
          onClick={() => onChange(value === LAB_STAGE.ATTENTION ? null : LAB_STAGE.ATTENTION)}
          className={cn(
            "flex w-full items-center gap-2 rounded-lg border px-4 py-2.5 text-left text-sm transition-colors",
            value === LAB_STAGE.ATTENTION
              ? "border-destructive bg-destructive/10"
              : "border-destructive/40 bg-destructive/5 hover:bg-destructive/10"
          )}
        >
          <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" />
          <span className="font-medium text-destructive">
            {attention} order{attention === 1 ? "" : "s"} need chasing
          </span>
          <span className="text-muted-foreground">— missed patients or phlebotomist no-shows</span>
          <span className="ml-auto text-xs text-muted-foreground">
            {value === LAB_STAGE.ATTENTION ? "Showing these" : "View"}
          </span>
        </button>
      )}
    </div>
  );
}
